import { ConsentPurpose, ConsentStatus, OfferChannel } from '@today/types'

/** Why Step 4 disables a channel (TO-125 BR-5); the catalog key `send.block.<value>` words it. */
export enum ChannelBlock {
	NO_CONSENT = 'noConsent',
	NO_EMAIL = 'noEmail',
	NO_MOBILE = 'noMobile',
}

/** One picker option: the channel, whether it may be ticked, and why not. */
export interface ChannelEligibility {
	channel: OfferChannel
	enabled: boolean
	reason: ChannelBlock | null
}

/** The consent fields the rule reads (a `ConsentDTO` from `GET /customers/{id}/consents`). */
export interface ConsentRow {
	channel: string
	purpose: string
	status: string
}

/** Whether the recipient has a primary email / mobile. */
export interface RecipientContacts {
	email: boolean
	mobile: boolean
}

/** The consent channel each offer channel needs (mirrors offer-service's send gate). */
const CONSENT_CHANNEL: Record<OfferChannel, string> = {
	[OfferChannel.EMAIL]: 'email',
	[OfferChannel.SMS]: 'sms',
	[OfferChannel.WHATSAPP]: 'whatsapp',
}

const hasContact = (channel: OfferChannel, contacts: RecipientContacts): boolean =>
	channel === OfferChannel.EMAIL ? contacts.email : contacts.mobile

const missingContact = (channel: OfferChannel): ChannelBlock =>
	channel === OfferChannel.EMAIL ? ChannelBlock.NO_EMAIL : ChannelBlock.NO_MOBILE

const hasMarketingConsent = (channel: OfferChannel, consents: readonly ConsentRow[]): boolean =>
	consents.some(
		(c) =>
			c.channel === CONSENT_CHANNEL[channel] &&
			c.purpose === ConsentPurpose.MARKETING &&
			c.status === ConsentStatus.GRANTED,
	)

/**
 * Step 4's channel picker (TO-125 REQ-104): every channel the tenant enabled, in
 * that order, disabled when the recipient has no primary contact for it, else
 * when they hold no granted MARKETING consent on it — the order the server
 * checks. A `null` input means "could not be read": nothing is disabled for that
 * reason and the send's 422 is the backstop.
 */
export function channelEligibility(
	channels: readonly OfferChannel[],
	consents: readonly ConsentRow[] | null,
	contacts: RecipientContacts | null,
): ChannelEligibility[] {
	return channels.map((channel) => {
		let reason: ChannelBlock | null = null
		if (contacts && !hasContact(channel, contacts)) reason = missingContact(channel)
		else if (consents && !hasMarketingConsent(channel, consents)) reason = ChannelBlock.NO_CONSENT
		return { channel, enabled: reason === null, reason }
	})
}
